import { ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { createMasterGridOptions } from '@app/Shared/grid/data-explorer-grid-options';
import { DedupService } from '@app/Services/dedup.service';
import { AlertService, AlertType } from '@app/Services/Utilities/alert.service';
import { iParsedEntity, iProperty } from '@app/Shared/interfaces/iEntityParsed';
import { pluralize } from '@app/Shared/util/stringManipulation';
import { AppState } from '@app/Store';
import * as DashboardActions from '@app/Store/actions/dashboard-actions';
import { selectToken } from '@app/Store/features/auth.feature';
import { selectColumnConfigForEntity } from '@app/Store/features/column-config.feature';
import { dashboardFeatureSelectors } from '@app/Store/features/dashboard.feature';
import { ActionsSubject, select, Store } from '@ngrx/store';
import {
    GridOptions,
    GridReadyEvent,
    IServerSideDatasource,
    RowDoubleClickedEvent,
    RowEditingStartedEvent,
    RowEditingStoppedEvent,
    RowSelectedEvent,
} from 'ag-grid-community';
import { Subscription } from 'rxjs';
import { distinctUntilChanged, filter, skip, switchMap, take } from 'rxjs/operators';
import { TDGridSettingsService } from '@app/Shared/common-components/td-grid/tdgrid-settings.service';

interface ColumnConfig {
    field: string;
    visible: boolean;
    sortable: boolean;
    defaultSort?: 'asc' | 'desc';
    width?: number;
    minWidth?: number;
    sortIndex?: number;
    pinned?: 'left' | 'right';
    headerName?: string;
    tooltip?: string;
}

@Component({
    selector: 'app-new-data-explorer-master-panel',
    templateUrl: './new-data-explorer-master-panel.component.html',
    styleUrls: ['./new-data-explorer-master-panel.component.scss'],
})
export class NewDataExplorerMasterPanelComponent implements OnInit, OnDestroy {
    @Input() isFullscreen = false;
    @Output() toggleFullscreen = new EventEmitter<void>();

    // Grid properties
    public masterGridOptions: GridOptions;
    public selectedEntity: iParsedEntity | null = null;
    public noRecordSelected = true;
    public gridServerSideDatasource: IServerSideDatasource;
    public suppressSelectEvent: boolean = false;
    public shouldReloadGrid: boolean = false;
    public currentGridId: string = 'new-master-grid';
    public aiSearchQuery: string = '';

    private subscription: Subscription = new Subscription();
    private masterGridApi: any;
    private columnConfigSub: Subscription;
    // Map to store original row data before editing starts (keyed by row node ID)
    private originalRowDataMap = new Map<string, any>();
    // Store filter model to preserve it during grid reload
    private savedFilterModel: any = null;
    // Track previous entity name to detect actual entity changes (not just reloads)
    private previousEntityName: string | null = null;

    constructor(
        private store: Store<AppState>,
        private cdr: ChangeDetectorRef,
        private dedupService: DedupService,
        private alertService: AlertService,
        private actions$: ActionsSubject,
        private gridSettingsService: TDGridSettingsService,
    ) {
        this.masterGridOptions = createMasterGridOptions(this.isFirstColumn);
        // Add context menu specifically for this grid
        this.masterGridOptions.getContextMenuItems = this.getContextMenuItems.bind(this);
        // Handle row editing started to make data mutable (fix read-only property error)
        this.masterGridOptions.onRowEditingStarted = this.onRowEditingStarted.bind(this);
    }
    ngOnInit(): void {
        // Subscribe to authentication and data loading
        this.subscription.add(
            this.store
                .pipe(
                    select(selectToken),
                    filter((token) => !!token),
                    switchMap(() => this.store.select(dashboardFeatureSelectors.selectEntities)),
                    filter((entities) => entities.length > 0),
                    switchMap(() => this.store.select(dashboardFeatureSelectors.selectMasterTables)),
                    filter((masterTables) => masterTables.length > 0),
                )
                .subscribe({
                    error: (err) => console.error('Error loading data:', err),
                }),
        );

        // Subscribe to dedup service for add to dedup actions
        this.subscription.add(
            this.dedupService.addToDedup$.subscribe((payload) => {
                this.store.dispatch(
                    DashboardActions.addDedupItem({
                        item: payload.rowData,
                        entityName: payload.entityName,
                    }),
                );
            }),
        );
        // Subscribe to selected entity
        this.subscription.add(
            this.store
                .pipe(select(dashboardFeatureSelectors.selectSelectedEntity))
                .pipe(distinctUntilChanged((prev, curr) => prev?.entityName === curr?.entityName))
                .subscribe((entity) => {
                    if (entity) {
                        this.aiSearchQuery = null;
                        // Only clear saved filter model when entity name actually changes (not during reload)
                        const currentEntityName = entity.entityName;
                        if (this.previousEntityName !== null && this.previousEntityName !== currentEntityName) {
                            // Entity name changed - clear saved filters
                            this.savedFilterModel = null;
                        }
                        this.previousEntityName = currentEntityName;

                        const selectedEntity = { ...entity };
                        this.noRecordSelected = false;
                        this.configureGridColumns(selectedEntity);
                        this.gridServerSideDatasource = this.createGridServerSideDatasource(selectedEntity.entityName);
                        this.selectedEntity = selectedEntity;

                        // Reload the grid if entity has changed
                        if (this.shouldReloadGrid) {
                            this.reloadMasterGrid();
                        }
                    } else {
                        this.clearMasterData();
                        this.selectedEntity = null;
                        this.gridServerSideDatasource = null;
                        this.noRecordSelected = true;
                        // Clear saved filter model when entity is cleared
                        this.savedFilterModel = null;
                        this.previousEntityName = null;
                    }
                }),
        );

        this.subscription.add(
            this.actions$
                .pipe(
                    filter(
                        (action: any) => action.type === DashboardActions.updateEntityRowDataSuccess.type || action.type === DashboardActions.updateEntityRowDataFailure.type,
                    ),
                )
                .subscribe((action: any) => {
                    if (action.type === DashboardActions.updateEntityRowDataFailure.type) {
                       return;
                    } else {
                        // Success - show optional success message
                        this.reloadMasterGrid();
                       return;
                    }
                }),
        );

        // Now handled per-entity when selectedEntity changes
    }

    ngOnDestroy(): void {
        this.subscription.unsubscribe();
        if (this.columnConfigSub) {
            this.columnConfigSub.unsubscribe();
        }
    }

    onToggleFullscreen(): void {
        this.toggleFullscreen.emit();
    }

    onRefreshMasterGrid(): void {
        if (this.noRecordSelected || !this.selectedEntity || this.shouldReloadGrid) {
            return;
        }
        this.reloadMasterGrid();
    }

    // Context menu configuration
    private getContextMenuItems = (params: any) => {
        const result = [];

        if (params.node && params.node.data && this.selectedEntity) {
            result.push({
                name: 'Add to Dedup',
                action: () => {
                    // Capture current displayed column order from the grid (visible columns)
                    const visibleColumns = params.columnApi?.getAllDisplayedColumns?.() || [];
                    const propertyOrder = visibleColumns.map((col: any) => col.getColId?.() ?? col.colId).filter((id: string) => !!id);
                    this.dedupService.triggerAddToDedup(params.node.data, this.selectedEntity.entityName, propertyOrder);
                    this.openDedupTab();
                },
                icon: '<i class="material-icons">find_replace</i>',
            });

            result.push({
                name: 'Update current row',
                action: () => {
                    // Select the row first
                    params.node.setSelected(true);
                    // The edit tab will automatically pick up the selected record
                    // We need to trigger tab selection in details panel
                    this.openEditRowTab();
                },
                icon: '<i class="material-icons">edit</i>',
            });
        }

        return result;
    };
    private openEditRowTab(): void {
        // Dispatch action to change selected tab to EditRow
        // We'll use a custom action or store the tab selection in state
        // For now, we'll use a simple approach with a service or event
        // Since we don't have a tab service, we'll use the store to manage tab state
        this.store.dispatch(DashboardActions.changeSelectedTab({ selectedTab: 'EditRow' }));
    }

    private openDedupTab(): void {
        // Dispatch action to change selected tab to Dedup
        this.store.dispatch(DashboardActions.changeSelectedTab({ selectedTab: 'Dedup' }));
    }

    // Grid event handlers
    onMasterGridReady = (event: GridReadyEvent): void => {
        this.masterGridApi = event.api;
        // Reset the reload flag after grid is ready
        this.shouldReloadGrid = false;

        // Restore saved filter model if available
        if (this.masterGridApi && this.savedFilterModel && Object.keys(this.savedFilterModel).length > 0) {
            // Wait for grid columns to be fully set up before restoring filters
            // Use setTimeout to ensure grid is fully initialized before applying filters
            setTimeout(() => {
                try {
                    // Restore filters (this will trigger data loading)
                    this.masterGridApi.setFilterModel(this.savedFilterModel);
                    this.masterGridApi.onFilterChanged();
                } catch (error) {
                    console.warn('Failed to restore filters:', error);
                }
            }, 900);
        }
    };

    onMasterRowSelect = (event: RowSelectedEvent): void => {
        if (this.suppressSelectEvent) return;
        const selectedRows = event.api.getSelectedRows();
        if (selectedRows.length > 0) {
            // This event is called two time, once on row select and once on unslect, we only need the call once.
            if (event.node.isSelected() === false) return;
            const selectedRow = selectedRows[0];
            this.store.dispatch(DashboardActions.storeSelectedRecords({ selectedRecords: selectedRows }));
            this.noRecordSelected = false;
            console.log('Master row selected:', selectedRow);
        } else {
            this.store.dispatch(DashboardActions.storeSelectedRecords({ selectedRecords: [] }));
            this.noRecordSelected = true;
        }
    };
    onRowDoubleClicked = (event: RowDoubleClickedEvent): void => {
        if (!event.node || !event.api || !this.selectedEntity) return;

        // Get the first editable column to start editing (skip ID field column)
        const columns = event.api.getAllDisplayedColumns();
        const firstEditableColumn = columns.find((col) => {
            const colDef = col.getColDef();
            const field = colDef.field;
            // Skip ID field column (same logic as dedup component)
            const isIdField = this.selectedEntity && this.selectedEntity.key === field;
            return colDef.editable !== false && colDef.field && !isIdField;
        });

        if (firstEditableColumn && firstEditableColumn.getColId()) {
            // With editType: 'fullRow', starting editing on any cell will make the entire row editable
            event.api.startEditingCell({
                rowIndex: event.node.rowIndex,
                colKey: firstEditableColumn.getColId(),
            });
        }
    };

    onRowEditingStarted = (event: RowEditingStartedEvent): void => {
        // Create a mutable copy of the row data to prevent "Cannot assign to read only property" error
        // This is necessary when data comes from immutable sources (e.g., NgRx store)
        if (event.node && event.node.data && this.selectedEntity) {
            const originalData = event.node.data;
            // Store the original data for comparison when editing stops
            // Use the entity key value as the identifier (should be unique and stable)
            const keyValue = originalData[this.selectedEntity.key];
            const nodeId = keyValue?.toString() || event.node.id || event.node.rowIndex?.toString() || Math.random().toString();
            this.originalRowDataMap.set(nodeId, JSON.parse(JSON.stringify(originalData)));

            // Create a deep copy to ensure the object is mutable
            const mutableData = JSON.parse(JSON.stringify(originalData));
            // Update the node's data reference to the mutable copy
            event.node.setData(mutableData);
        }
    };

    onMasterRowEditingStopped = (event: RowEditingStoppedEvent): void => {
        const updatedData = event.data;

        if (!this.selectedEntity || !updatedData) {
            console.log('Row editing stopped: missing selectedEntity or updatedData');
            return;
        }

        // Get the node ID using the entity key value (should match what we stored in onRowEditingStarted)
        const keyValue = updatedData[this.selectedEntity.key];
        const nodeId = keyValue?.toString() || event.node.id || event.node.rowIndex?.toString() || Math.random().toString();
        const originalData = this.originalRowDataMap.get(nodeId);

        console.log('Row editing stopped:', { updatedData, originalData, nodeId, keyValue });

        // Clean up the stored original data
        this.originalRowDataMap.delete(nodeId);

        // Check if data actually changed by comparing with the original data we stored
        const hasChanges = originalData
            ? Object.keys(updatedData).some((key) => {
                  const newValue = updatedData[key];
                  const oldValue = originalData[key];
                  // Handle different data types for comparison
                  if (newValue === oldValue) return false;
                  // Convert to string for date/object comparison if needed
                  if (newValue != null && oldValue != null && typeof newValue === 'object' && typeof oldValue === 'object') {
                      return JSON.stringify(newValue) !== JSON.stringify(oldValue);
                  }
                  return true;
              })
            : false;

        console.log('Has changes:', hasChanges);

        if (hasChanges && this.selectedEntity && originalData) {
            const entityName = pluralize(this.selectedEntity.entityName);
            const keyValue = updatedData[this.selectedEntity.key];

            if (keyValue !== undefined && keyValue !== null) {
                const editablePropNames = new Set(
                    this.selectedEntity.properties.filter((p) => p.readOnly === false || (p.readOnly == null && p.name !== this.selectedEntity!.key)).map((p) => p.name),
                );
                const payload = Object.keys(updatedData)
                    .filter((k) => editablePropNames.has(k))
                    .reduce((acc, k) => ({ ...acc, [k]: updatedData[k] }), {} as Record<string, any>);

                console.log('Dispatching update action:', { entityName, key: keyValue, rowData: payload });
                this.store.dispatch(
                    DashboardActions.updateEntityRowData({
                        entityName: entityName,
                        key: keyValue,
                        rowData: payload,
                    }),
                );

                // Refresh the row to show updated data (will be updated by reducer on success)
                if (this.masterGridApi) {
                    this.masterGridApi.refreshCells({ rowNodes: [event.node], force: true });
                }
            } else {
                this.alertService.notify(AlertType.Error, 'Cannot update row: key value is missing', 'Update Error');
            }
        }
    };

    onAISearchQueryApplied = (query: string): void => {
        console.log('AI Search query applied:', query);
        this.aiSearchQuery = query;
        console.log('AI Search query applied:', query);

        // If we have a valid query and the grid is ready, reload the data
        if (query && this.selectedEntity && this.masterGridApi) {
            this.reloadMasterGrid();
        } else if (!query && this.selectedEntity && this.masterGridApi) {
            // Clear AI search query - reload with normal filters
            this.reloadMasterGrid();
        }
    };

    // Helper method for grid options
    private isFirstColumn = (params: any): boolean => {
        const displayedColumns = params?.columnApi?.getAllDisplayedColumns() || [];
        return displayedColumns[0] === params.column;
    };

    // Grid configuration methods
    private configureGridColumns(entity: iParsedEntity): void {
        this.shouldReloadGrid = true;
        this.currentGridId = `new-master-grid-${entity.entityName}`;

        if (this.masterGridApi) {
            this.masterGridApi.destroy();
            this.masterGridApi = null;
        }

        if (this.columnConfigSub) {
            this.columnConfigSub.unsubscribe();
        }
        // Subscribe for just this entity config
        this.columnConfigSub = this.store.select(selectColumnConfigForEntity(entity.entityName)).subscribe((columnConfig: ColumnConfig[]) => {
            let updatedEntity = { ...entity };
            if (columnConfig.length > 0) {
                const columnOrder = NewDataExplorerMasterPanelComponent.getColumnOrderFromConfig(columnConfig);
                updatedEntity = this.sortEntityProperties(updatedEntity, columnOrder);
            }

            const sortConfig = NewDataExplorerMasterPanelComponent.getSortConfigFromConfig(columnConfig);
            const sortConfigMap = new Map(sortConfig.map((s: any) => [s.colId, s]));
            this.masterGridOptions.columnDefs = updatedEntity.properties.map((property: iProperty) => {
                const config = columnConfig.find((col) => col.field === property.name);
                const sortInfo: any = sortConfigMap.get(property.name);
                const isIdField = updatedEntity.key === property.name;
                const isEditable = property.readOnly === false || (property.readOnly == null && !isIdField);
                return {
                    field: property.name,
                    headerName: config?.headerName || property.uiLabel || property.name,
                    sortable: config?.sortable ?? true,
                    editable: isEditable,
                    filter: this.convertEdmToAgGridType(property.type),
                    filterParams: {
                        buttons: ['apply', 'reset'],
                        closeOnApply: true,
                    },
                    width: config?.width,
                    pinned: config?.pinned,
                    tooltip: config?.tooltip,
                    resizable: true,
                    minWidth: config?.minWidth,
                    hide: config ? !config.visible : false, // Set hide based on visible flag
                    sort: sortInfo?.sort,
                    sortIndex: sortInfo?.sortIndex,
                };
            });
            if (this.masterGridApi) {
                this.masterGridApi.setColumnDefs(this.masterGridOptions.columnDefs);
            }
        });
    }
    private sortEntityProperties(entity: any, sortOrder: any) {
        // Create a map of the sort order for quick lookup
        const orderMap = new Map(sortOrder.map((key, index) => [key, index]));

        // Sort the properties based on the sort order
        entity.properties = entity.properties.slice().sort((a: any, b: any) => {
            const aIndex: any = orderMap.has(a.name) ? orderMap.get(a.name) : Infinity;
            const bIndex: any = orderMap.has(b.name) ? orderMap.get(b.name) : Infinity;

            return aIndex - bIndex;
        });

        return entity;
    }

    private convertEdmToAgGridType(edmType: string): any {
        const edmToAgGridMap: { [key: string]: any } = {
            'Edm.Int32': 'agNumberColumnFilter',
            'Edm.String': 'agTextColumnFilter',
            'Edm.Boolean': false,
            'Edm.Decimal': 'agNumberColumnFilter',
            'Edm.Double': 'agNumberColumnFilter',
            'Edm.Single': 'agNumberColumnFilter',
            'Edm.Int16': 'agNumberColumnFilter',
            'Edm.Int64': 'agNumberColumnFilter',
            'Edm.Byte': 'agNumberColumnFilter',
            'Edm.DateTime': 'agDateColumnFilter',
            'Edm.DateTimeOffset': 'agDateColumnFilter',
            'Edm.Date': 'agDateColumnFilter',
            'Edm.Time': 'agTextColumnFilter',
            'Edm.Guid': 'agTextColumnFilter',
            'Edm.Binary': 'agTextColumnFilter',
        };
        return edmToAgGridMap[edmType] || 'agTextColumnFilter';
    }
    private reloadMasterGrid(): void {
        // Save current filter model before reloading to preserve it
        if (this.masterGridApi) {
            const filterModel = this.masterGridApi.getFilterModel();
            // Only save filter model if it exists and has filters
            if (filterModel && Object.keys(filterModel).length > 0) {
                this.savedFilterModel = filterModel;
            } else {
                // If no filters, keep existing saved filters (don't clear them)
                // Only clear if we explicitly want to reset
            }
        }

        // Force change detection to remove the grid from DOM
        this.shouldReloadGrid = true;
        this.cdr.detectChanges();

        // Wait for next tick to ensure grid is removed, then show it again
        setTimeout(() => {
            this.shouldReloadGrid = false;
            this.cdr.detectChanges();
        }, 300);
    }

    private clearMasterData() {
        if (this.masterGridApi) {
            this.masterGridApi.setRowData([]);
        }
        // Reset grid state when clearing data
        this.shouldReloadGrid = false;
        this.currentGridId = 'new-master-grid';
        this.cdr.detectChanges();
    }

    private createGridServerSideDatasource(entityName: string): IServerSideDatasource {
        const dtServerSide: IServerSideDatasource = {
            getRows: (params) => {
                const queryParams = this.buildOdataQuery(params);

                this.store.dispatch(
                    DashboardActions.loadSelectedEntityRowData({
                        entityName: pluralize(entityName),
                        queryParams: queryParams,
                    }),
                );
                const subscription = this.store
                    .select(dashboardFeatureSelectors.selectEntityRowDataWithCount)
                    .pipe(skip(1))
                    .subscribe((value: any) => {
                        // Create mutable copies of row data to prevent read-only property errors
                        const originalRowData = value?.rowData || [];
                        const mutableRowData = originalRowData.map((row: any) => JSON.parse(JSON.stringify(row)));

                        params.success({ rowData: mutableRowData, rowCount: value.totalCount });
                        const rowData = mutableRowData;
                        const hasData = rowData != null && rowData.length > 0;

                        if (this.masterGridApi) {
                            this.masterGridApi.setRowData(rowData);
                            this.suppressSelectEvent = true;
                            this.masterGridApi.forEachNode((node: any, index: number) => {
                                if (index === 0) {
                                    node.setSelected(true); // Select the first row
                                }
                            });
                            setTimeout(() => {
                                this.suppressSelectEvent = false;
                                this.cdr.detectChanges();
                            }, 700);
                        }

                        if (rowData.length > 0) {
                            const selectedRow = rowData[0];
                            this.store.dispatch(DashboardActions.storeSelectedRecords({ selectedRecords: [selectedRow] }));
                            this.noRecordSelected = false;
                            this.cdr.detectChanges();
                        } else {
                            this.store.dispatch(DashboardActions.storeSelectedRecords({ selectedRecords: [] }));
                            this.noRecordSelected = true;
                            this.cdr.detectChanges();
                        }

                        subscription.unsubscribe();
                    });
            },
        };
        return dtServerSide;
    }
    private buildOdataQuery(params: any) {
        const { startRow, endRow, filterModel, sortModel } = params.request;

        const $top = endRow - startRow;
        const $skip = startRow;

        const $orderby = sortModel.map((sort: any) => `${sort.colId} ${sort.sort}`).join(',');

        // If we have an AI Search query, use it instead of building filters from the grid
        if (this.aiSearchQuery && this.aiSearchQuery.trim()) {
            // Parse the AI Search query to extract existing parameters and add our pagination/sorting
            const existingParams = new URLSearchParams(this.aiSearchQuery);

            // Build the final query parameters
            const queryParams = [
                '$count=true',
                $top ? `$top=${$top}` : '',
                $skip ? `$skip=${$skip}` : '',
                $orderby ? `$orderby=${$orderby}` : '',
                // Use the AI Search query as the main filter
                this.aiSearchQuery.includes('$filter=') ? this.aiSearchQuery : `$filter=${this.aiSearchQuery}`,
            ]
                .filter((param) => param)
                .join('&');

            return queryParams;
        }
        const buildFilterCondition = (condition: any, field: string): string => {
            if (condition.filterType === 'text') {
                const value = condition.filter ?? '';
                const toLowerField = `tolower(${field})`;
                const toLowerValue = `tolower('${value}')`;

                switch (condition.type) {
                    case 'contains':
                        return `contains(${toLowerField}, ${toLowerValue})`;
                    case 'startsWith':
                        return `startswith(${toLowerField}, ${toLowerValue})`;
                    case 'endsWith':
                        return `endswith(${toLowerField}, ${toLowerValue})`;
                    case 'equals':
                        return `${toLowerField} eq ${toLowerValue}`;
                    case 'notEqual':
                        return `${toLowerField} ne ${toLowerValue}`;
                    default:
                        return '';
                }
            } else if (condition.filterType === 'number') {
                const operatorMap: { [key: string]: string } = {
                    equals: 'eq',
                    lessThan: 'lt',
                    greaterThan: 'gt',
                    lessThanOrEqual: 'le',
                    greaterThanOrEqual: 'ge',
                    notEqual: 'ne',
                    inRange: 'inRange',
                };
                const type = condition.type;
                if (type === 'inRange') {
                    const from = condition.filter;
                    const to = condition.filterTo;
                    if (from == null || to == null) return '';
                    return `${field} ge ${from} and ${field} le ${to}`;
                }
                const filterVal = condition.filter;
                if (filterVal == null || filterVal === '') return '';
                const op = operatorMap[type];
                if (!op) return '';
                return `${field} ${op} ${filterVal}`;
            } else if (condition.filterType === 'date') {
                if (condition.type === 'inRange') {
                    const from = condition.dateFrom;
                    const to = condition.dateTo;
                    if (from == null || to == null) return '';
                    const dateFrom = new Date(from).toISOString();
                    const dateTo = new Date(to).toISOString();
                    return `${field} ge ${dateFrom} and ${field} le ${dateTo}`;
                }
                const dateFrom = condition.dateFrom;
                if (dateFrom == null || dateFrom === '') return '';
                const date = new Date(dateFrom).toISOString();
                switch (condition.type) {
                    case 'equals':
                        return `${field} eq ${date}`;
                    case 'notEqual':
                        return `${field} ne ${date}`;
                    case 'greaterThan':
                        return `${field} gt ${date}`;
                    case 'lessThan':
                        return `${field} lt ${date}`;
                    case 'greaterThanOrEqual':
                        return `${field} ge ${date}`;
                    case 'lessThanOrEqual':
                        return `${field} le ${date}`;
                    default:
                        return '';
                }
            }

            return '';
        };

        const buildFilterGroup = (filters: any[], logicalOperator: string, field: string): string => {
            logicalOperator = logicalOperator.toLowerCase();
            return filters
                .map((condition: any) => buildFilterCondition(condition, field))
                .filter((filter) => filter)
                .join(` ${logicalOperator} `);
        };

        const $filter = filterModel
            ? Object.entries(filterModel)
                  .reduce((acc: any[], [field, condition]: [string, any]) => {
                      if (Array.isArray(condition.conditions)) {
                          // Handle grouped conditions with 'and'/'or'
                          const groupFilter = buildFilterGroup(condition.conditions, condition.operator || 'and', field);
                          if (groupFilter) acc.push(`(${groupFilter})`);
                      } else {
                          const singleFilter = buildFilterCondition(condition, field);
                          if (singleFilter) acc.push(singleFilter);
                      }
                      return acc;
                  }, [])
                  .join(' and ')
            : '';

        const queryParams = [
            '$count=true',
            $top ? `$top=${$top}` : '',
            $skip ? `$skip=${$skip}` : '',
            $orderby ? `$orderby=${$orderby}` : '',
            $filter ? `$filter=${$filter}` : '',
        ]
            .filter((param) => param)
            .join('&');

        return queryParams;
    }

    private static getColumnOrderFromConfig(config: ColumnConfig[]): string[] {
        // Return all columns to maintain order, visibility is handled by hide property
        return config.map((col) => col.field);
    }

    private static getSortConfigFromConfig(config: ColumnConfig[]): any[] {
        return config
            .filter((col) => col.visible && col.defaultSort)
            .sort((a, b) => {
                const aIndex = a.sortIndex !== undefined ? a.sortIndex : Infinity;
                const bIndex = b.sortIndex !== undefined ? b.sortIndex : Infinity;
                return aIndex - bIndex;
            })
            .map((col) => ({
                colId: col.field,
                sort: col.defaultSort,
                sortIndex: col.sortIndex,
            }));
    }
}
